import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, CircularProgress, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Stack, Card, CardContent, Divider, Chip
} from '@mui/material';
import { getRaces, addRace, updateRace, deleteRace, getSkis, getAvailableWeatherData } from '../api';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import WeatherDataForm from '../components/WeatherDataForm';
import WeatherDataDisplay from '../components/WeatherDataDisplay';
import WeatherFilter from '../components/WeatherFilter';
import { filterByWeatherConditions, hasActiveWeatherFilters } from '../utils/weatherFilterUtils';
import { useAuth } from '../AuthContext';

const TECHNIQUES = [
  { value: 'classic', label: 'Classic' },
  { value: 'skate', label: 'Skate' },
];
const PERFORMANCES = [
  'Excellent', 'Good', 'OK', 'Bad', 'Terrible'
];

const cleanRaceForm = (form) => ({
  ...form,
  temperature: form.temperature === '' ? null : Number(form.temperature),
  humidity: form.humidity === '' ? null : Number(form.humidity),
  distance: form.distance === '' ? null : Number(form.distance),
  ski_id: form.ski_id === '' ? null : Number(form.ski_id),
});

function RaceForm({ open, onClose, onSave, initial, skis, defaultTechnique }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState(initial || {
    name: '', location: '', date: '', ski_id: '', technique: defaultTechnique || '', performance: '',
    time_of_day: '',
    // Standardized weather fields
    snow_temperature_c: '', air_temperature_c: '', snow_humidity_doser: '', air_humidity_rh: '',
    clouds_sky_parts: '', visibility: '', wind: '', precipitation: '',
    artificial_snow: '', natural_snow: '', grain_size: '', snow_humidity: '',
    track_hardness: '', track_shininess: '', track_dirtiness: '',
    // Race data
    distance: '', comments: ''
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [availableWeatherData, setAvailableWeatherData] = useState([]);
  const [loadingWeatherData, setLoadingWeatherData] = useState(false);
  const [dateRangeDays, setDateRangeDays] = useState(7);

  useEffect(() => {
    setForm(initial || {
      name: '', location: '', date: '', ski_id: '', technique: defaultTechnique || '', performance: '',
      time_of_day: '',
      // Standardized weather fields
      snow_temperature_c: '', air_temperature_c: '', snow_humidity_doser: '', air_humidity_rh: '',
      clouds_sky_parts: '', visibility: '', wind: '', precipitation: '',
      artificial_snow: '', natural_snow: '', grain_size: '', snow_humidity: '',
      track_hardness: '', track_shininess: '', track_dirtiness: '',
      // Race data
      distance: '', comments: ''
    });
    setError('');
    setSaving(false);
  }, [open, initial, defaultTechnique]);

  const loadAvailableWeatherData = useCallback(async () => {
    setLoadingWeatherData(true);
    try {
      const data = await getAvailableWeatherData(dateRangeDays);
      setAvailableWeatherData(data);
    } catch (err) {
      console.error('Failed to load available weather data:', err);
    } finally {
      setLoadingWeatherData(false);
    }
  }, [dateRangeDays]);

  useEffect(() => {
    if (open && !initial) {
      loadAvailableWeatherData();
    }
  }, [open, initial, loadAvailableWeatherData]);

  const handleSelectWeatherData = (entry) => {
    if (!entry) {
      return;
    }
    setForm(f => ({
      ...f,
      location: entry.location || f.location,
      snow_temperature_c: entry.snow_temperature_c ?? '',
      air_temperature_c: entry.air_temperature_c ?? '',
      snow_humidity_doser: entry.snow_humidity_doser ?? '',
      air_humidity_rh: entry.air_humidity_rh ?? '',
      clouds_sky_parts: entry.clouds_sky_parts ?? '',
      visibility: entry.visibility || '',
      wind: entry.wind || '',
      precipitation: entry.precipitation || '',
      artificial_snow: entry.artificial_snow || '',
      natural_snow: entry.natural_snow || '',
      grain_size: entry.grain_size || '',
      snow_humidity: entry.snow_humidity || '',
      track_hardness: entry.track_hardness || '',
      track_shininess: entry.track_shininess || '',
      track_dirtiness: entry.track_dirtiness || ''
    }));
  };

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{initial ? 'Edit Race' : 'Add Race'}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          
          <TextField label="Race Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
          <TextField label="Location" name="location" value={form.location} onChange={handleChange} required fullWidth />
          <TextField label="Date" name="date" type="date" value={form.date ? form.date.split('T')[0] : ''} onChange={handleChange} InputLabelProps={{ shrink: true }} required fullWidth />
          <TextField select label="Ski" name="ski_id" value={form.ski_id} onChange={handleChange} fullWidth>
            <MenuItem value="">Other/Comment</MenuItem>
            {skis.map(ski => (
              <MenuItem key={ski.id} value={ski.id}>#{ski.ski_number} - {ski.brand}</MenuItem>
            ))}
          </TextField>
          <TextField select label="Technique" name="technique" value={form.technique} onChange={handleChange} required fullWidth>
            {TECHNIQUES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <TextField select label="Performance" name="performance" value={form.performance} onChange={handleChange} fullWidth>
            {PERFORMANCES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </TextField>
          <TextField label="Time of Day" name="time_of_day" value={form.time_of_day} onChange={handleChange} fullWidth />
          <TextField label="Distance (km)" name="distance" type="number" value={form.distance} onChange={handleChange} fullWidth />
          
          {!initial && availableWeatherData.length > 0 && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Use Team Weather Data</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  select
                  label="Date Range"
                  value={dateRangeDays}
                  onChange={(e) => {
                    setDateRangeDays(Number(e.target.value));
                  }}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value={3}>3 Days</MenuItem>
                  <MenuItem value={7}>1 Week</MenuItem>
                  <MenuItem value={14}>2 Weeks</MenuItem>
                  <MenuItem value={21}>3 Weeks</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Select Weather Data Entry"
                  value=""
                  onChange={(e) => {
                    const entry = availableWeatherData.find(w => w.id === Number(e.target.value));
                    handleSelectWeatherData(entry);
                  }}
                  size="small"
                  sx={{ flex: 1, minWidth: 200 }}
                  disabled={loadingWeatherData}
                >
                  <MenuItem value="">None</MenuItem>
                  {availableWeatherData.map((entry) => (
                    <MenuItem key={entry.id} value={entry.id}>
                      {entry.team_name} - {entry.location || 'No location'} ({new Date(entry.date).toLocaleDateString()})
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          )}

          {/* Weather Data Form */}
          <WeatherDataForm
            weatherData={form}
            onChange={setForm}
            title="Weather Conditions"
            compact={true}
          />
          <TextField label="Comments" name="comments" value={form.comments} onChange={handleChange} multiline rows={2} fullWidth />
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" color="primary" disabled={saving} onClick={handleSubmit}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RaceDetailModal({ open, onClose, race, onEdit, onDelete, skis }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  if (!race) return null;
  const skiMap = Object.fromEntries((skis || []).map(ski => [ski.id, ski.ski_number]));
  const skiDisplay = race.ski_id ? (skiMap[race.ski_id] ? `#${skiMap[race.ski_id]}` : 'Ski no longer part of fleet') : 'Ski no longer part of fleet';
  const detailRows = [
    { label: 'Name', value: race.name },
    { label: 'Location', value: race.location },
    { label: 'Date', value: race.date ? race.date.split('T')[0] : '' },
    { label: 'Ski', value: skiDisplay },
    { label: 'Technique', value: race.technique },
    { label: 'Performance', value: race.performance },
    { label: 'Time of Day', value: race.time_of_day },
    { label: 'Distance', value: race.distance },
    { label: 'Comments', value: race.comments },
  ];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: theme.palette.text.primary }}>Race Details</DialogTitle>
      <DialogContent sx={{ bgcolor: theme.palette.background.paper, color: theme.palette.text.primary }}>
        <TableContainer component={Paper} sx={{ my: 2, borderRadius: 2, boxShadow: 1, bgcolor: theme.palette.background.paper }}>
          <Table size="small" sx={{ bgcolor: theme.palette.background.paper }}>
            <TableBody>
              {detailRows.map((row, i) => (
                <TableRow key={row.label} sx={{ background: i % 2 === 0 ? theme.palette.action.hover : theme.palette.background.paper }}>
                  <TableCell sx={{ fontWeight: 600, width: '35%', color: theme.palette.text.primary }}>{row.label}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word', color: theme.palette.text.primary }}>{row.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Weather Data Display */}
        <WeatherDataDisplay
          weatherData={race}
          title="Weather Conditions"
          compact={true}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2, bgcolor: theme.palette.background.paper }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => onEdit(race)} variant="contained">Edit</Button>
          <Button 
            color="error" 
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(race.id);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete(race.id);
            }}
            variant="outlined"
            sx={{ touchAction: 'manipulation' }}
          >
            Delete
          </Button>
          <Button onClick={onClose} variant="text">Close</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function RacesTable({ races, onEdit, onDelete, onDetail, skis }) {
  const skiMap = Object.fromEntries(skis.map(ski => [ski.id, ski.ski_number]));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Mobile card view
  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {races.map((race) => {
          const skiDisplay = race.ski_id ? (skiMap[race.ski_id] ? `#${skiMap[race.ski_id]}` : 'Ski no longer part of fleet') : 'Ski no longer part of fleet';
          return (
            <Card key={race.id} sx={{ boxShadow: 2 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {race.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDetail(race);
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(race);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDelete(race.id);
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onDelete(race.id);
                      }}
                      sx={{ touchAction: 'manipulation' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Location:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{race.location || '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Date:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{race.date ? race.date.split('T')[0] : '-'}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Ski:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{skiDisplay}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Technique:</Typography>
                    <Chip label={race.technique} size="small" sx={{ textTransform: 'capitalize' }} />
                  </Box>
                  {race.performance && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Performance:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{race.performance}</Typography>
                    </Box>
                  )}
                  {race.comments && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Comments:</Typography>
                      <Typography variant="body2">{race.comments}</Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }
  
  // Desktop table view
  return (
    <TableContainer
      component={Paper}
      sx={{
        mb: 4,
        borderRadius: 2,
        boxShadow: 2,
        width: '100%',
      }}
    >
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ background: '#f5f5f5' }}>
            <TableCell>Name</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Ski</TableCell>
            <TableCell>Technique</TableCell>
            <TableCell>Performance</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {races.map((race) => {
            const skiDisplay = race.ski_id ? (skiMap[race.ski_id] ? `#${skiMap[race.ski_id]}` : 'Ski no longer part of fleet') : 'Ski no longer part of fleet';
            return (
              <TableRow key={race.id} hover>
                <TableCell>{race.name}</TableCell>
                <TableCell>{race.location}</TableCell>
                <TableCell>{race.date ? race.date.split('T')[0] : ''}</TableCell>
                <TableCell>{skiDisplay}</TableCell>
                <TableCell>{race.technique}</TableCell>
                <TableCell>{race.performance}</TableCell>
                <TableCell>{race.comments}</TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <IconButton size="small" onClick={() => onDetail(race)}><InfoIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => onEdit(race)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(race.id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function RacesSection() {
  const [races, setRaces] = useState([]);
  const [filteredRaces, setFilteredRaces] = useState([]);
  const [skis, setSkis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, race: null });
  const [detail, setDetail] = useState({ open: false, race: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, raceId: null });
  const [deleting, setDeleting] = useState(false);
  const [weatherFilters, setWeatherFilters] = useState({});
  const { user } = useAuth();

  const fetchRacesAndSkis = async () => {
    setLoading(true);
    try {
      const [racesData, skisData] = await Promise.all([getRaces(), getSkis()]);
      setRaces(racesData);
      setFilteredRaces(racesData);
      setSkis(skisData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRacesAndSkis(); }, []);

  // Apply weather filters whenever races or filters change
  useEffect(() => {
    if (hasActiveWeatherFilters(weatherFilters)) {
      const filtered = filterByWeatherConditions(races, weatherFilters);
      setFilteredRaces(filtered);
    } else {
      setFilteredRaces(races);
    }
  }, [races, weatherFilters]);

  const handleWeatherFilterChange = (filters) => {
    setWeatherFilters(filters);
  };

  const handleClearWeatherFilters = () => {
    setWeatherFilters({});
  };

  const handleAdd = () => setModal({ open: true, race: null });
  const handleEdit = race => setModal({ open: true, race });
  const handleDelete = raceId => setConfirmDelete({ open: true, raceId });
  const confirmDeleteRace = async () => {
    setDeleting(true);
    try {
      await deleteRace(confirmDelete.raceId);
      setConfirmDelete({ open: false, raceId: null });
      fetchRacesAndSkis();
    } catch (err) {
      console.error('Error deleting race:', err);
      // Keep dialog open on error so user can try again or cancel
    } finally {
      setDeleting(false);
    }
  };
  const handleSave = async (form) => {
    const cleaned = cleanRaceForm(form);
    if (modal.race) {
      await updateRace(modal.race.id, cleaned);
    } else {
      await addRace(cleaned);
    }
    setModal({ open: false, race: null });
    fetchRacesAndSkis();
  };
  const handleDetail = race => setDetail({ open: true, race });
  const handleDetailEdit = race => {
    setDetail({ open: false, race: null });
    setModal({ open: true, race });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ mt: 2, px: isMobile ? 1 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 1 : 0 }}>
        <Typography variant="h6" sx={{ flex: isMobile ? '1 1 100%' : 'none' }}>Races</Typography>
        <Button variant="contained" onClick={handleAdd} startIcon={<EventIcon />} fullWidth={isMobile} size={isMobile ? 'medium' : 'medium'}>Add Race</Button>
      </Box>
      
      {/* Weather Filter */}
      <WeatherFilter 
        onFilterChange={handleWeatherFilterChange}
        onClearFilters={handleClearWeatherFilters}
        compact={false}
        title="Filter Races by Weather Conditions"
      />
      
      {loading ? <CircularProgress /> : (
        filteredRaces.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {races.length === 0 ? 'No races yet' : 'No races match your weather filters'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {races.length === 0 
                ? 'Add your first race to track your competition history and results.'
                : 'Try adjusting your weather filters to see more races.'
              }
            </Typography>
            <Button
              variant="contained"
              onClick={races.length === 0 ? handleAdd : handleClearWeatherFilters}
              startIcon={<EventIcon />}
            >
              {races.length === 0 ? 'Add Your First Race' : 'Clear Weather Filters'}
            </Button>
          </Paper>
        ) : (
          <RacesTable races={filteredRaces} onEdit={handleEdit} onDelete={handleDelete} onDetail={handleDetail} skis={skis} />
        )
      )}
      <RaceForm open={modal.open} onClose={() => setModal({ open: false, race: null })} onSave={handleSave} initial={modal.race} skis={skis} defaultTechnique={user?.default_technique} />
      <RaceDetailModal open={detail.open} onClose={() => setDetail({ open: false, race: null })} race={detail.race} onEdit={handleDetailEdit} onDelete={handleDelete} skis={skis} />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete.open} onClose={() => !deleting && setConfirmDelete({ open: false, raceId: null })}>
        <DialogTitle>Delete Race?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this race? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDelete({ open: false, raceId: null })} 
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={confirmDeleteRace} 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 